import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import BottomNav from './components/BottomNav';

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { pageview } from './analytics';
import API from './api/axios';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import SwipePage from './pages/SwipePage';
import MatchesPage from './pages/MatchesPage';
import ChatPage from './pages/ChatPage';
import ProfileViewPage from './pages/ProfileViewPage';
import SettingsPage from './pages/SettingsPage';
import EditProfilePage from './pages/EditProfilePage';
import PremiumPage from './pages/PremiumPage';
import DonatePage from './pages/DonatePage';
import DonateCallbackPage from './pages/DonateCallbackPage';
import ManageSubscriptionPage from './pages/ManageSubscriptionPage';
import { NotificationProvider } from './context/NotificationContext';
import NotificationsPage from './pages/NotificationsPage';
import NotificationToast from './components/NotificationToast';
import { useLocationTracker } from './hooks/useLocation';
import SubscriptionCallbackPage from './pages/SubscriptionCallbackPage';
import MyProfilePage from './pages/MyProfilePage';
import AdminRoute from './components/AdminRoute';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminDonationsPage from './pages/admin/AdminDonationsPage';
import AdminPhotosPage from './pages/admin/AdminPhotosPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminBroadcastPage from './pages/admin/AdminBroadcastPage';
import AdminBillingResyncPage from './pages/admin/AdminBillingResyncPage';
import InstallPrompt from './components/InstallPrompt';
import PrivacyPage   from './pages/PrivacyPage';
import TermsPage     from './pages/TermsPage';
import CommunityPage from './pages/CommunityPage';
import CookieBanner from './components/CookieBanner';
import LikesPage from './pages/LikesPage';
import useWebSocket from './hooks/useWebSocket';
import LandingPage from './pages/LandingPage';  
import ContactPage from './pages/ContactPage';    // ← New import

// New component to handle WebSocket inside AuthProvider
function WebSocketProvider({ children }) {
  const { token } = useAuth();   // Get token from your AuthContext
  useWebSocket(token);                 // ← This starts the WebSocket

  return children;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AuthenticatedLayout({ children }) {
  return (
    <ProtectedRoute>
      <div style={{ paddingBottom: '64px' }}>
        {children}
        <BottomNav />
      </div>
    </ProtectedRoute>
  );
}

function GoogleAnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);

  return null;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes (no nav) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/email-verification" element={<EmailVerificationPage />} />
      <Route path="/privacy"              element={<PrivacyPage />} />
      <Route path="/terms"                element={<TermsPage />} />
      <Route path="/community-guidelines" element={<CommunityPage />} />
       <Route path="/landing" element={<LandingPage />} />
       <Route path="/contact" element={<ContactPage />} />

      {/* Auth-only, no nav (onboarding flow) */}
      <Route
        path="/profile-setup"
        element={
          <ProtectedRoute>
            <ProfileSetupPage />
          </ProtectedRoute>
        }
      />

      {/* Auth-only, with nav (main app) */}
      <Route path="/swipe" element={<AuthenticatedLayout><SwipePage /></AuthenticatedLayout>} />
      <Route path="/matches" element={<AuthenticatedLayout><MatchesPage /></AuthenticatedLayout>} />
      <Route path="/profile" element={<AuthenticatedLayout><MyProfilePage /></AuthenticatedLayout>} />
      <Route path="/settings" element={<AuthenticatedLayout><SettingsPage /></AuthenticatedLayout>} />
      <Route path="/notifications" element={<AuthenticatedLayout><NotificationsPage /></AuthenticatedLayout>} />
      <Route path="/profile/edit" element={<ProtectedRoute><EditProfilePage /></ProtectedRoute>} />
      <Route path="/premium" element={<ProtectedRoute><PremiumPage /></ProtectedRoute>} />
      <Route path="/donate" element={<ProtectedRoute><DonatePage /></ProtectedRoute>} />
      <Route path="/donate/callback" element={<ProtectedRoute><DonateCallbackPage /></ProtectedRoute>} />
      <Route path="/subscription/manage" element={<ProtectedRoute><ManageSubscriptionPage /></ProtectedRoute>} />
      <Route path="/subscription/callback" element={<SubscriptionCallbackPage />} />
      <Route path="/likes" element={<AuthenticatedLayout><LikesPage /></AuthenticatedLayout>} />

      <Route path="/chat/:id" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/users/:id" element={<ProtectedRoute><ProfileViewPage /></ProtectedRoute>} />

      <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
      <Route path="/admin/reports" element={<AdminRoute><AdminReportsPage /></AdminRoute>} />
      <Route path="/admin/photos" element={<AdminRoute><AdminPhotosPage /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
      <Route path="/admin/donations" element={<AdminRoute><AdminDonationsPage /></AdminRoute>} />
      <Route path="/admin/broadcast" element={<AdminRoute><AdminBroadcastPage /></AdminRoute>} />
      <Route path="/admin/billing-resync" element={<AdminRoute><AdminBillingResyncPage /></AdminRoute>} />
      
   <Route path="/welcome" element={<LandingPage />} />
  <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  useLocationTracker();

  useEffect(() => {
    let visitorId = null;

    try {
      visitorId = localStorage.getItem('godly_match_visitor_id');

      if (!visitorId) {
        visitorId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        localStorage.setItem(
          'godly_match_visitor_id',
          visitorId
        );
      }
    } catch (e) {
      // If localStorage is unavailable, create a temporary ID.
      visitorId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    API.post('/api/analytics/visit', {
      visitor_id: visitorId,
    }).catch(() => {
      // Analytics should never break the website.
    });
  }, []);

  return (
    <AuthProvider>
      <NotificationProvider>
        <BrowserRouter>
         <GoogleAnalyticsTracker />
          <InstallPrompt />
          <CookieBanner />
          <NotificationToast />
          
          {/* WebSocket runs for all authenticated users */}
          <WebSocketProvider>
            <AppRoutes />
          </WebSocketProvider>
        </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;